import { DataSource } from '@/models/dataSource';
import { useGetDataSourcesQuery } from '@/services/dataSources';
import { useGetConnectorFlowInstancesQuery, useGetConnectorInstancesQuery } from '@/services/endpoints/workspace-connectors';
import { useMemo } from 'react';
import { USE_MOCK_SOURCE_CONNECTOR_LINKED_DATASOURCES, buildMockLinkedDataSources } from './mockLinkedDataSources';
import { SourceConnectorCatalogItem } from './useSourceConnectorCatalogItems';

const DATA_SOURCES_FETCH_LIMIT = 100;

type UseSourceConnectorLinkedDataSourcesArgs = {
  workspaceId: string;
  item: SourceConnectorCatalogItem;
};

export const useSourceConnectorLinkedDataSources = ({ workspaceId, item }: UseSourceConnectorLinkedDataSourcesArgs) => {
  const { connector, dataSourceType, isPlaceholder } = item;

  const { data: instances = [], isFetching: isFetchingInstances } = useGetConnectorInstancesQuery({ workspaceId });
  const { data: dataSourcesResponse, isFetching: isFetchingDataSources } = useGetDataSourcesQuery({
    workspaceId,
    limit: DATA_SOURCES_FETCH_LIMIT,
    offset: 0,
  });

  const connectorInstanceId = useMemo(
    () => instances.find((instance) => instance.connectorId === connector.id)?.id,
    [connector.id, instances]
  );

  const { data: flowInstances = [], isFetching: isFetchingFlowInstances } = useGetConnectorFlowInstancesQuery(
    { workspaceId, connectorInstanceId: connectorInstanceId ?? '' },
    { skip: !connectorInstanceId }
  );

  const linkedDataSources = useMemo((): DataSource[] => {
    if (USE_MOCK_SOURCE_CONNECTOR_LINKED_DATASOURCES && isPlaceholder) {
      return buildMockLinkedDataSources(dataSourceType);
    }

    if (!connectorInstanceId) {
      return [];
    }

    const flowInstanceIds = new Set(flowInstances.map((flowInstance) => flowInstance.id));
    const items = dataSourcesResponse?.items ?? [];

    return items.filter(
      (dataSource) =>
        dataSource.connectorFlowInstanceId != null && flowInstanceIds.has(dataSource.connectorFlowInstanceId)
    );
  }, [connectorInstanceId, dataSourceType, dataSourcesResponse?.items, flowInstances, isPlaceholder]);

  return {
    linkedDataSources,
    connectorInstanceId,
    isLoading: isFetchingInstances || isFetchingDataSources || isFetchingFlowInstances,
  };
};
